import logger from '../utils/logger';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Search, 
  Calendar, 
  Eye, 
  ArrowRight,
  Filter,
  Grid,
  List,
  Coffee,
  Star
} from 'lucide-react';
import NavigationHeader from '../components/NavigationHeader';
import blogService from '../services/blogService';
import toast from 'react-hot-toast';

const Blog = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  
  // Debug: let's see what's happening
  logger.debug('Blog Debug:');
  logger.debug('Current language:', i18n.language);
  logger.debug('blog.title:', t('blog.title'));
  logger.debug('Direct translation test:', t('navigation.home'));
  
  const [blogs, setBlogs] = useState([]);
  const [featuredBlogs, setFeaturedBlogs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [viewMode, setViewMode] = useState('grid');

  // Data loaders wrapped with useCallback to stabilize dependencies
  const loadBlogs = useCallback(async () => {
    try {
      setLoading(true);
      if (selectedCategory === 'all') {
        const response = await blogService.getPublishedBlogs(currentPage, 9);
        if (response.success) {
          setBlogs(response.data);
          setTotalPages(response.pagination.totalPages);
        } else {
          toast.error(response.error);
        }
      } else {
        const response = await blogService.getBlogsByCategory(selectedCategory, currentPage, 9);
        if (response.success) {
          setBlogs(response.data);
          setTotalPages(response.pagination.totalPages);
        } else {
          toast.error(response.error);
        }
      }
    } catch (error) {
      toast.error(t('blog.failedToLoadBlogs'));
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, currentPage, t]);

  const searchBlogs = useCallback(async () => {
    if (!searchTerm.trim()) return;
    try {
      setLoading(true);
      const response = await blogService.searchBlogs(searchTerm, currentPage, 9);
      if (response.success) {
        setBlogs(response.data);
        setTotalPages(response.pagination.totalPages);
      } else {
        toast.error(response.error);
      }
    } catch (error) {
      toast.error(t('blog.failedToSearch'));
    } finally {
      setLoading(false);
    }
  }, [searchTerm, currentPage, t]);

  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);
      const [featuredResponse, categoriesResponse] = await Promise.all([
        blogService.getFeaturedBlogs(),
        blogService.getAllCategories()
      ]);
      if (featuredResponse.success) {
        setFeaturedBlogs(featuredResponse.data);
      }
      if (categoriesResponse.success) {
        setCategories(categoriesResponse.data);
      }
      await loadBlogs();
    } catch (error) {
      toast.error(t('blog.failedToLoadData'));
    } finally {
      setLoading(false);
    }
  }, [loadBlogs, t]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  useEffect(() => {
    if (searchTerm.trim()) {
      searchBlogs();
    } else {
      loadBlogs();
    }
  }, [searchTerm, currentPage, selectedCategory, searchBlogs, loadBlogs]);

  // functions moved above and memoized with useCallback

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(0);
    searchBlogs();
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setCurrentPage(0);
    setSearchTerm('');
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getCategoryLabel = (category) => {
    return t(`blog.categories.${category.toLowerCase()}`, category.replace('_', ' '));
  };

  if (loading && blogs.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="text-center text-white">
          <Coffee className="text-[#a15507] w-16 h-16 mb-6 animate-pulse mx-auto" />
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#a15507] mx-auto mb-4"></div>
          <p className="text-gray-300">{t('blog.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      <NavigationHeader />
      
      {/* Hero Section */}
      <div className="pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#a15507]/20 to-blue-500/20 backdrop-blur-sm border border-[#a15507]/30 rounded-full px-6 py-3 mb-8">
            <Coffee className="w-5 h-5 text-[#a15507]" />
            <span className="text-white font-medium">{t('blog.title')}</span>
          </div>
          
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 tracking-tight">
            {i18n.language === 'es' ? 'Blog de Café' : 
             i18n.language === 'fr' ? 'Blog Café' : 
             i18n.language === 'ar' ? 'مدونة القهوة' : 
             t('blog.title')}
          </h1>
          
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-12 leading-relaxed">
            {i18n.language === 'es' ? 'Descubre historias increíbles, consejos e ideas sobre la cultura del café' : 
             i18n.language === 'fr' ? 'Découvrez des histoires incroyables, des conseils et des idées sur la culture du café' : 
             i18n.language === 'ar' ? 'اكتشف قصص مذهلة ونصائح ورؤى حول ثقافة القهوة' : 
             t('blog.subtitle')}
          </p>

          {/* Search Bar */}
          <form onSubmit={handleSearchSubmit} className="max-w-2xl mx-auto mb-12">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-6 h-6" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={i18n.language === 'es' ? 'Buscar artículos...' : 
                           i18n.language === 'fr' ? 'Rechercher des articles...' : 
                           i18n.language === 'ar' ? 'البحث في المقالات...' : 
                           t('blog.searchPlaceholder')}
                className="w-full pl-12 pr-6 py-4 bg-gray-800/50 backdrop-blur-sm border border-gray-600 rounded-2xl text-white placeholder-gray-400 focus:outline-none focus:border-[#a15507] focus:ring-2 focus:ring-[#a15507]/20 text-lg"
              />
              <button
                type="submit"
                className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-[#a15507] hover:bg-[#924e06] text-white px-6 py-2 rounded-xl font-semibold transition-all duration-300"
              >
                Search
              </button>
            </div>
          </form>
        </div>

        {/* Filters */}
        <div className="max-w-7xl mx-auto px-6 mb-12">
          <div className="flex flex-wrap items-center justify-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-400" />
              <select
                value={selectedCategory}
                onChange={(e) => handleCategoryChange(e.target.value)}
                className="bg-gray-800/50 backdrop-blur-sm border border-gray-600 rounded-xl px-4 py-2 text-white focus:outline-none focus:border-[#a15507]"
              >
                <option value="all">
                  {i18n.language === 'es' ? 'Todas las Categorías' : 
                   i18n.language === 'fr' ? 'Toutes les Catégories' : 
                   i18n.language === 'ar' ? 'جميع الفئات' : 
                   t('blog.allCategories')}
                </option>
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {getCategoryLabel(category)}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2 bg-gray-800/50 backdrop-blur-sm border border-gray-600 rounded-xl p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-lg transition-all duration-300 ${
                  viewMode === 'grid' 
                    ? 'bg-[#a15507] text-white shadow-lg' 
                    : 'text-gray-400 hover:text-white hover:bg-gray-700'
                }`}
              >
                <Grid className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-lg transition-all duration-300 ${
                  viewMode === 'list' 
                    ? 'bg-[#a15507] text-white shadow-lg' 
                    : 'text-gray-400 hover:text-white hover:bg-gray-700'
                }`}
              >
                <List className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Featured Blogs */}
        <div className="max-w-7xl mx-auto px-6">
          {featuredBlogs.length > 0 && (
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-white mb-8 text-center">
                <Star className="inline w-8 h-8 text-[#a15507] mr-2" />
                {t('blog.featuredPosts')}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {featuredBlogs.slice(0, 3).map((blog) => (
                  <article 
                    key={blog.id} 
                    className="group cursor-pointer bg-gray-800/40 backdrop-blur-sm rounded-2xl border border-gray-600 hover:border-[#a15507] transition-all duration-300 hover:scale-105 overflow-hidden" 
                    onClick={() => navigate(`/blog/${blog.slug}`)}
                  >
                    {blog.featuredImageUrl && (
                      <div className="aspect-video bg-gray-700 overflow-hidden">
                        <img 
                          src={blog.featuredImageUrl} 
                          alt={blog.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                      </div>
                    )}
                    <div className="p-6">
                      <div className="flex items-center gap-2 mb-4">
                        <span className="px-3 py-1 bg-[#a15507] text-white text-sm rounded-full font-medium">
                          {getCategoryLabel(blog.category)}
                        </span>
                        <span className="px-3 py-1 bg-gradient-to-r from-green-500 to-emerald-500 text-white text-sm rounded-full font-medium flex items-center gap-1">
                          <Star className="w-3 h-3" />
                          {t('blog.featured')}
                        </span>
                      </div>
                      <h3 className="text-xl font-bold text-white mb-3 group-hover:text-[#a15507] transition-colors line-clamp-2">
                        {blog.title}
                      </h3>
                      <p className="text-gray-400 text-sm mb-4 line-clamp-3 leading-relaxed">
                        {blog.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-sm text-gray-500">
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            <span>{formatDate(blog.publishedAt || blog.createdAt)}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Eye className="w-4 h-4" />
                            <span>{blog.viewCount}</span>
                          </div>
                        </div>
                        <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform duration-300 text-[#a15507]" />
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          )}

          {/* Blog Posts */}
          {loading ? (
            <div className="text-center py-20">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-[#a15507] border-t-transparent mx-auto mb-8"></div>
              <p className="text-gray-300 text-xl">{t('blog.loadingPosts')}</p>
            </div>
          ) : blogs.length > 0 ? (
            <>
              <div className={`grid gap-8 ${viewMode === 'grid' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1 max-w-4xl mx-auto'}`}>
                {blogs.map((blog) => (
                  <article 
                    key={blog.id} 
                    className={`group cursor-pointer bg-gray-800/40 backdrop-blur-sm rounded-2xl border border-gray-600 hover:border-gray-500 transition-all duration-300 hover:scale-105 overflow-hidden ${
                      viewMode === 'list' ? 'flex flex-row' : ''
                    }`} 
                    onClick={() => navigate(`/blog/${blog.slug}`)}
                  >
                    {blog.featuredImageUrl && (
                      <div className={`bg-gray-700 overflow-hidden ${
                        viewMode === 'list' ? 'w-64 h-40 flex-shrink-0' : 'aspect-video'
                      }`}>
                        <img 
                          src={blog.featuredImageUrl} 
                          alt={blog.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                      </div>
                    )}
                    <div className={`${viewMode === 'list' ? 'flex-1' : ''} p-6`}>
                      <div className="flex items-center gap-2 mb-4">
                        <span className="px-3 py-1 bg-[#a15507] text-white text-sm rounded-full font-medium">
                          {getCategoryLabel(blog.category)}
                        </span>
                      </div>
                      <h3 className="text-xl font-bold text-white mb-3 group-hover:text-[#a15507] transition-colors line-clamp-2">
                        {blog.title}
                      </h3>
                      <p className="text-gray-400 text-sm mb-4 line-clamp-3 leading-relaxed">
                        {blog.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-sm text-gray-500">
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            <span>{formatDate(blog.publishedAt || blog.createdAt)}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Eye className="w-4 h-4" />
                            <span>{blog.viewCount}</span>
                          </div>
                        </div>
                        <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform duration-300 text-[#a15507]" />
                      </div>
                    </div>
                  </article>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-2 mt-16">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 0}
                    className="px-6 py-3 bg-gray-800/50 backdrop-blur-sm text-white rounded-xl disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-700 transition-all duration-300 border border-gray-600"
                  >
                    {t('blog.previous')}
                  </button>
                  
                  {[...Array(totalPages)].map((_, index) => (
                    <button
                      key={index}
                      onClick={() => handlePageChange(index)}
                      className={`px-4 py-3 rounded-xl transition-all duration-300 border ${
                        currentPage === index
                          ? 'bg-[#a15507] text-white border-[#a15507] shadow-lg'
                          : 'bg-gray-800/50 backdrop-blur-sm text-white border-gray-600 hover:bg-gray-700'
                      }`}
                    >
                      {index + 1}
                    </button>
                  ))}
                  
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages - 1}
                    className="px-6 py-3 bg-gray-800/50 backdrop-blur-sm text-white rounded-xl disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-700 transition-all duration-300 border border-gray-600"
                  >
                    {t('blog.next')}
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-20">
              <div className="text-8xl mb-6">☕</div>
              <h3 className="text-3xl font-bold text-white mb-4">
                {i18n.language === 'es' ? 'No se encontraron artículos' : 
                 i18n.language === 'fr' ? 'Aucun article trouvé' : 
                 i18n.language === 'ar' ? 'لم يتم العثور على مقالات' : 
                 t('blog.noPostsFound')}
              </h3>
              <p className="text-gray-400 text-lg max-w-md mx-auto">
                {i18n.language === 'es' ? 'Intenta ajustar tu búsqueda o navega por diferentes categorías' : 
                 i18n.language === 'fr' ? 'Essayez d\'ajuster votre recherche ou parcourez différentes catégories' : 
                 i18n.language === 'ar' ? 'حاول تعديل بحثك أو تصفح فئات مختلفة' : 
                 t('blog.noPostsDescription')}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Blog;
